import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError, setToken } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Menu, LogOut, Radio, KeyRound } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";

export const Header = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: integrations } = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => (await api.get("/integrations")).data,
  });
  const connected = (integrations || []).filter((i) => i.status === "connected").length;
  const lastSync = (integrations || []).map((i) => i.last_sync).filter(Boolean).sort().reverse()[0];

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const submitPw = async () => {
    if (nw !== confirm) { toast.error("New passwords do not match"); return; }
    if (nw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data.access_token) setToken(data.access_token);
      toast.success("Password changed successfully");
      setPwOpen(false); setCur(""); setNw(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[#1E2D4A] bg-[#0B1120]/90 px-4 backdrop-blur-md sm:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden text-slate-300" onClick={onMenu} data-testid="mobile-menu-button">
        <Menu className="h-5 w-5" />
      </Button>

      <div className="hidden items-center gap-2 rounded-full border border-[#1E2D4A] bg-[#111A2E] px-3 py-1.5 sm:flex" data-testid="data-freshness-indicator">
        <Radio className={`h-3.5 w-3.5 ${connected ? "text-emerald-400" : "text-slate-500"}`} />
        <span className="text-xs text-slate-400">
          {connected > 0 ? (
            <>Live aggregated · <span className="text-slate-300">{fmtDateTime(lastSync)}</span></>
          ) : (
            "Manual data only · No product integrations connected"
          )}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <div className="text-xs font-semibold text-slate-200">{user?.name || "Founder"}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{(user?.role || "").replace("_", " ")}</div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 text-xs font-bold text-slate-900" data-testid="user-menu-button">
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-[#1E2D4A] bg-[#111A2E] text-slate-200">
            <DropdownMenuLabel className="text-slate-400">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#1E2D4A]" />
            <DropdownMenuItem onClick={() => setPwOpen(true)} className="cursor-pointer focus:bg-[#17233B]" data-testid="change-password-menu-item">
              <KeyRound className="mr-2 h-4 w-4" /> Change password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-rose-300 focus:bg-rose-500/10 focus:text-rose-200" data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-cyan-400" /> Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-400">Current password</Label>
              <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">New password (min 8 characters)</Label>
              <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPwOpen(false)} className="text-slate-400" data-testid="change-password-cancel">Cancel</Button>
            <Button onClick={submitPw} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="change-password-submit">
              {saving ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};
